import React, { Component } from 'react';
import Modal from '../../components/Modal';
import KanbanIssueCard from './KanbanIssueCard';
import ChapterIssueKanbanCard from './ChapterIssueKanbanCard';
import ProjectIssueKanbanCard from './ProjectIssueKanbanCard';
import MoveIssueToChapterModal from './MoveIssueToChapterModal';
import { urlMap, getAuthHeaders } from '../../../utils';

const LS_KEY_SEVERITY = 'kanban_filterSeverity';
const LS_KEY_SCOPE = 'kanban_filterScope';

const COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'selected', label: 'Selected for Work', wipLimit: 3 },
  { key: 'in_progress', label: 'In Progress', wipLimit: 1 },
  { key: 'testing', label: 'Testing' },
  { key: 'done', label: 'Done' },
];

/**
 * KanbanModal — Kanban board for project issues.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - project: object
 *  - onIssuesCountChanged?: () => void
 */
export default class KanbanModal extends Component {
  state = {
    issues: [],
    loading: false,
    error: null,
    // Drag state
    dragIssueId: null,
    dragOverColumn: null,
    // Detail overlay
    detailIssue: null,
    // Add issue form
    newTitle: '',
    newDescription: '',
    newSeverity: 'medium',
    newChapterId: '',
    createBusy: false,
    createError: null,
    // Chapters for create form
    chapters: [],
    // Filters (severity & scope persisted in localStorage)
    filterSeverity: localStorage.getItem(LS_KEY_SEVERITY) || '',
    filterChapter: '',
    filterScope: localStorage.getItem(LS_KEY_SCOPE) || '',
    // Move to chapter modal
    moveToChapterIssue: null,
    moveToChapterInitialChapterId: null,
    // WIP limit warning banner
    wipWarning: null,
    wipWarningNeedsFilterClear: false,
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadIssues();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadIssues();
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      this.setState({
        issues: [],
        error: null,
        dragIssueId: null,
        dragOverColumn: null,
        detailIssue: null,
        newTitle: '',
        newDescription: '',
        newSeverity: 'medium',
        newChapterId: '',
        createBusy: false,
        createError: null,
        chapters: [],
        filterChapter: '',
        moveToChapterIssue: null,
        moveToChapterInitialChapterId: null,
      });
    }
  }

  /* ────── Data ────── */

  loadIssues = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return;
    this.setState({ loading: true, error: null });
    try {
      const [issuesRes, projectIssuesRes, chaptersRes] = await Promise.all([
        fetch(urlMap.getProjectIssues(projectId), {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
        fetch(urlMap.getStructuralThematicIssues(projectId), {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
        fetch(urlMap.listChapters(projectId), {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
      ]);
      if (!issuesRes.ok) throw new Error('Failed to fetch issues');
      const data = await issuesRes.json();
      // Filter out pending/dismissed AI suggestions; map approved → backlog for kanban
      const chapterIssues = (Array.isArray(data) ? data : [])
        .filter((i) => i.status !== 'pending' && i.status !== 'dismissed')
        .map((i) => ({ ...(i.status === 'approved' ? { ...i, status: 'backlog' } : i), _source: 'chapter' }));

      // Merge structural/thematic issues (project_issues table)
      let projectIssues = [];
      if (projectIssuesRes.ok) {
        const piData = await projectIssuesRes.json();
        projectIssues = (Array.isArray(piData) ? piData : [])
          .filter((i) => i.status !== 'dismissed')
          .map((i) => ({
            ...i,
            status: i.status === 'pending' ? 'backlog' : (i.status === 'approved' ? 'backlog' : i.status),
            _source: i.scope || 'structural',
          }));
      }

      const issues = [...chapterIssues, ...projectIssues];
      const chapters = chaptersRes.ok ? await chaptersRes.json() : [];
      this.setState({ issues, chapters: Array.isArray(chapters) ? chapters : [], loading: false });
    } catch (e) {
      this.setState({ error: e.message, loading: false });
    }
  };

  /* ────── Callbacks for card components ────── */

  handleIssueUpdated = (updatedIssue) => {
    // Enforce WIP limit: reject moves when column is full
    if (this.isColumnFull(updatedIssue.status, updatedIssue.id)) {
      this.showWipWarning(updatedIssue.status);
      // Revert: reload to undo optimistic update in child
      this.loadIssues();
      return;
    }
    this.setState((s) => ({
      issues: s.issues.map((i) => (i.id === updatedIssue.id ? updatedIssue : i)),
      detailIssue: s.detailIssue?.id === updatedIssue.id ? updatedIssue : s.detailIssue,
    }));
    this.props.onIssuesCountChanged?.();
  };

  handleIssueDeleted = (issueId) => {
    this.setState((s) => ({
      issues: s.issues.filter((i) => i.id !== issueId),
      detailIssue: s.detailIssue?.id === issueId ? null : s.detailIssue,
    }));
    this.props.onIssuesCountChanged?.();
  };

  handlePurgeAllIssues = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return;
    if (!window.confirm('Are you sure you want to delete ALL issues for this project? This cannot be undone.')) return;
    this.setState({ loading: true });
    try {
      const res = await fetch(urlMap.purgeProjectIssues(projectId), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Purge failed');
      this.setState({ issues: [], detailIssue: null, loading: false });
      this.props.onIssuesCountChanged?.();
    } catch (e) {
      this.setState({ error: e.message, loading: false });
    }
  };

  createIssue = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return;
    const title = this.state.newTitle.trim();
    if (!title) {
      this.setState({ createError: 'Title is required.' });
      return;
    }
    this.setState({ createBusy: true, createError: null });
    try {
      const res = await fetch(urlMap.createProjectIssue(projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          title,
          description: this.state.newDescription.trim(),
          severity: this.state.newSeverity,
          ...(this.state.newChapterId ? { chapter_id: this.state.newChapterId } : {}),
        }),
      });
      if (!res.ok) throw new Error('Failed to create issue');
      const created = await res.json();
      this.setState((s) => ({
        issues: [created, ...s.issues],
        newTitle: '',
        newDescription: '',
        newSeverity: 'medium',
        newChapterId: '',
        createBusy: false,
      }));
      this.props.onIssuesCountChanged?.();
    } catch (e) {
      this.setState({ createBusy: false, createError: e.message });
    }
  };

  /* ────── Drag & Drop ────── */

  handleDragStart = (e, issueId) => {
    this.setState({ dragIssueId: issueId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', issueId);
  };

  handleDragEnd = () => {
    this.setState({ dragIssueId: null, dragOverColumn: null });
  };

  handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (this.state.dragOverColumn !== columnKey) {
      this.setState({ dragOverColumn: columnKey });
    }
  };

  handleDragLeave = (e, columnKey) => {
    // Only clear if actually leaving the column
    if (this.state.dragOverColumn === columnKey) {
      this.setState({ dragOverColumn: null });
    }
  };

  showWipWarning = (columnKey) => {
    const { filterSeverity, filterChapter, filterScope, issues } = this.state;
    const col = COLUMNS.find((c) => c.key === columnKey);

    // Check if the blocking issue(s) are visible under the current filters
    const inProgressIssues = issues.filter((i) => i.status === columnKey);
    const visibleBlockers = inProgressIssues.filter((i) => {
      if (filterSeverity && i.severity !== filterSeverity) return false;
      if (filterScope) {
        if (filterScope === 'chapter' && (i._source === 'structural' || i._source === 'thematic')) return false;
        if (filterScope === 'structural' && i._source !== 'structural') return false;
        if (filterScope === 'thematic' && i._source !== 'thematic') return false;
      }
      if (filterChapter) {
        const isProject = !i.chapter_id || i.chapter_number === 0 || i.chapter_number === null;
        const chKey = isProject ? 'project' : String(i.chapter_number);
        if (chKey !== filterChapter) return false;
      }
      return true;
    });

    const hasFilters = filterSeverity || filterChapter || filterScope;
    const hasHiddenBlockers = hasFilters && visibleBlockers.length === 0 && inProgressIssues.length > 0;

    const msg = hasHiddenBlockers
      ? `Clear your filters — there's already an issue in "${col?.label}" hidden by the current filter.`
      : `For the best work experience, focus on one thing at a time.`;

    if (this._wipWarningTimer) clearTimeout(this._wipWarningTimer);
    this.setState({ wipWarning: msg, wipWarningNeedsFilterClear: hasHiddenBlockers });
    this._wipWarningTimer = setTimeout(() => this.setState({ wipWarning: null, wipWarningNeedsFilterClear: false }), 5000);
  };

  isColumnFull = (columnKey, excludeIssueId) => {
    const col = COLUMNS.find((c) => c.key === columnKey);
    if (!col?.wipLimit) return false;
    const count = this.state.issues.filter(
      (i) => i.status === columnKey && i.id !== excludeIssueId
    ).length;
    return count >= col.wipLimit;
  };

  handleDrop = (e, columnKey) => {
    e.preventDefault();
    const issueId = e.dataTransfer.getData('text/plain');
    this.setState({ dragIssueId: null, dragOverColumn: null });
    if (!issueId) return;
    const issue = this.state.issues.find((i) => i.id === issueId);
    if (!issue || issue.status === columnKey) return;
    if (this.isColumnFull(columnKey, issueId)) {
      this.showWipWarning(columnKey);
      return;
    }
    const projectId = this.props.project?.id;
    if (!projectId) return;

    const isProjectIssue = issue._source === 'structural' || issue._source === 'thematic';

    // Optimistic update
    this.setState((s) => ({
      issues: s.issues.map((i) => (i.id === issueId ? { ...i, status: columnKey } : i)),
    }));

    const url = isProjectIssue
      ? urlMap.updateStructuralThematicIssueStatus(projectId, issueId)
      : urlMap.updateProjectIssueStatus(projectId, issueId);

    fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ status: columnKey }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then((updated) => {
        this.setState((s) => ({
          issues: s.issues.map((i) =>
            i.id === issueId ? { ...updated, _source: issue._source } : i
          ),
        }));
        this.props.onIssuesCountChanged?.();
      })
      .catch(() => this.loadIssues());
  };

  /* ────── Render ────── */

  renderCard(issue) {
    const projectId = this.props.project?.id;
    const isDragging = this.state.dragIssueId === issue.id;
    const isProjectIssue = issue._source === 'structural' || issue._source === 'thematic';
    const CardComponent = isProjectIssue ? ProjectIssueKanbanCard : ChapterIssueKanbanCard;

    return (
      <CardComponent
        key={issue.id}
        issue={issue}
        projectId={projectId}
        isDragging={isDragging}
        onDragStart={(e) => this.handleDragStart(e, issue.id)}
        onDragEnd={this.handleDragEnd}
        onUpdated={this.handleIssueUpdated}
        onDeleted={this.handleIssueDeleted}
        onDetailClick={(detailIssue) => this.setState({ detailIssue })}
        onMoveToChapter={(iss) => this.setState({ moveToChapterIssue: iss, moveToChapterInitialChapterId: null })}
      />
    );
  }

  renderDetailOverlay() {
    const { detailIssue, chapters } = this.state;
    if (!detailIssue) return null;

    // Find the latest version from state
    const issue = this.state.issues.find((i) => i.id === detailIssue.id) || detailIssue;
    // Card components pass _moveIssue, _saveIssue, _deleteIssue via onDetailClick
    const { _moveIssue, _saveIssue, _deleteIssue } = detailIssue;
    const isChapterIssue = issue._source === 'chapter';
    const chapterOptions = Array.isArray(chapters)
      ? chapters
        .filter((ch) => !ch?.archived_at)
        .sort((a, b) => (a?.chapter_number ?? 0) - (b?.chapter_number ?? 0))
      : [];

    return (
      <div
        className="kanban-detail-overlay"
        onClick={() => this.setState({ detailIssue: null })}
      >
        <div
          className="kanban-detail-panel"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="kanban-detail-close"
            onClick={() => this.setState({ detailIssue: null })}
          >
            ×
          </button>
          <KanbanIssueCard
            issue={issue}
            onSave={_saveIssue}
            onDelete={_deleteIssue}
            chapterOptions={isChapterIssue ? chapterOptions : []}
            onOpenChapterMoveModal={() => {
              this.setState({
                moveToChapterIssue: issue,
                moveToChapterInitialChapterId: null,
                detailIssue: null,
              });
            }}
          />
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Move to
            </label>
            <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
              {COLUMNS.map((col) => {
                const wipBlocked = col.key !== issue.status && this.isColumnFull(col.key, issue.id);
                return (
                  <button
                    key={col.key}
                    type="button"
                    className="issue-card-btn"
                    style={col.key === issue.status
                      ? { color: '#3b82f6', borderColor: '#3b82f6', fontWeight: 600 }
                      : wipBlocked
                        ? { color: 'var(--text-3)', borderColor: 'var(--border, rgba(255,255,255,0.08))', opacity: 0.5 }
                        : { color: 'var(--text-2)', borderColor: 'var(--border, rgba(255,255,255,0.12))' }}
                    onClick={() => {
                      if (col.key !== issue.status && !wipBlocked && _moveIssue) _moveIssue(col.key);
                    }}
                    disabled={col.key === issue.status || wipBlocked}
                    title={wipBlocked ? 'WIP limit reached — finish the current issue first' : col.label}
                  >
                    {col.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isOpen, onClose } = this.props;
    const { issues, loading, error, dragOverColumn, filterSeverity, filterChapter, filterScope } = this.state;

    // Build chapter options from issues
    const chapterOptions = [];
    const seenChapters = new Set();
    for (const issue of issues) {
      const isProject = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
      const key = isProject ? 'project' : String(issue.chapter_number);
      if (!seenChapters.has(key)) {
        seenChapters.add(key);
        chapterOptions.push({ value: key, label: isProject ? 'Project' : `Ch ${issue.chapter_number}` });
      }
    }
    chapterOptions.sort((a, b) => {
      if (a.value === 'project') return -1;
      if (b.value === 'project') return 1;
      return Number(a.value) - Number(b.value);
    });

    // Apply filters
    const filtered = issues.filter((issue) => {
      if (filterSeverity && issue.severity !== filterSeverity) return false;
      if (filterScope) {
        if (filterScope === 'chapter' && (issue._source === 'structural' || issue._source === 'thematic')) return false;
        if (filterScope === 'structural' && issue._source !== 'structural') return false;
        if (filterScope === 'thematic' && issue._source !== 'thematic') return false;
      }
      if (filterChapter) {
        const isProject = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
        const chKey = isProject ? 'project' : String(issue.chapter_number);
        if (chKey !== filterChapter) return false;
      }
      return true;
    });

    const issuesByColumn = {};
    for (const col of COLUMNS) issuesByColumn[col.key] = [];
    for (const issue of filtered) {
      const bucket = issuesByColumn[issue.status] || issuesByColumn.backlog;
      bucket.push(issue);
    }

    // Sort within each column by severity
    const sevRank = { critical: 0, high: 1, medium: 2, low: 3 };
    for (const key of Object.keys(issuesByColumn)) {
      issuesByColumn[key].sort((a, b) => {
        const rA = sevRank[a.severity] ?? 9;
        const rB = sevRank[b.severity] ?? 9;
        return rA - rB || String(a.title || '').localeCompare(String(b.title || ''));
      });
    }

    const innerContent = (
      <>
      <div className="kanban-modal-content">
          <div className="kanban-modal-header">
            <span className="kanban-modal-title">Kanban Board</span>
            {!loading && !error && (
              <div className="kanban-filters">
                <div className="kanban-filter-group">
                  <label className="kanban-filter-label">Scope</label>
                  <select
                    className="kanban-filter-select"
                    value={filterScope}
                    onChange={(e) => {
                      const v = e.target.value;
                      localStorage.setItem(LS_KEY_SCOPE, v);
                      this.setState({ filterScope: v });
                    }}
                  >
                    <option value="">All</option>
                    <option value="chapter">Chapter</option>
                    <option value="structural">Structural</option>
                    <option value="thematic">Thematic</option>
                  </select>
                </div>
                <div className="kanban-filter-group">
                  <label className="kanban-filter-label">Severity</label>
                  <select
                    className="kanban-filter-select"
                    value={filterSeverity}
                    onChange={(e) => {
                      const v = e.target.value;
                      localStorage.setItem(LS_KEY_SEVERITY, v);
                      this.setState({ filterSeverity: v });
                    }}
                  >
                    <option value="">All</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div className="kanban-filter-group">
                  <label className="kanban-filter-label">Chapter</label>
                  <select
                    className="kanban-filter-select"
                    value={filterChapter}
                    onChange={(e) => this.setState({ filterChapter: e.target.value })}
                  >
                    <option value="">All</option>
                    {chapterOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {(filterSeverity || filterChapter || filterScope) && (
                  <button
                    type="button"
                    className="kanban-filter-clear"
                    onClick={() => {
                      localStorage.removeItem(LS_KEY_SEVERITY);
                      localStorage.removeItem(LS_KEY_SCOPE);
                      this.setState({ filterSeverity: '', filterChapter: '', filterScope: '' });
                    }}
                  >
                    Clear filters
                  </button>
                )}
                {issues.length > 0 && (
                  <button
                    type="button"
                    className="kanban-purge-btn"
                    onClick={this.handlePurgeAllIssues}
                    title="Delete all issues for this project"
                  >
                    Purge All
                  </button>
                )}
              </div>
            )}
          </div>

          {this.state.wipWarning && (
            <div className="kanban-wip-banner">
              <span>{this.state.wipWarning}</span>
              {this.state.wipWarningNeedsFilterClear && (
                <button
                  type="button"
                  className="kanban-wip-banner-clear"
                  onClick={() => {
                    localStorage.removeItem(LS_KEY_SEVERITY);
                    localStorage.removeItem(LS_KEY_SCOPE);
                    this.setState({ filterSeverity: '', filterChapter: '', filterScope: '', wipWarning: null, wipWarningNeedsFilterClear: false });
                  }}
                >
                  Clear filters
                </button>
              )}
              <button
                type="button"
                className="kanban-wip-banner-dismiss"
                onClick={() => this.setState({ wipWarning: null })}
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          {loading && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-3)' }}>
              <div className="agent-spinner" aria-hidden="true" />
              <span style={{ marginLeft: 8 }}>Loading issues…</span>
            </div>
          )}
          {error && (
            <div style={{ padding: 16, color: '#ef4444' }}>Error: {error}</div>
          )}

          {!loading && !error && (
            <div className="kanban-board">
              {COLUMNS.map((col) => {
                const colIssues = issuesByColumn[col.key];
                const wipLimit = col.wipLimit;
                // WIP count and full-check use unfiltered issues to stay consistent with isColumnFull
                const trueWipCount = wipLimit ? issues.filter((i) => i.status === col.key).length : null;
                const wipFull = wipLimit && trueWipCount >= wipLimit;
                return (
                  <div
                    key={col.key}
                    className={`kanban-column kanban-column--${col.key}${wipFull ? ' kanban-column--wip-full' : ''}`}
                    onDragOver={(e) => this.handleDragOver(e, col.key)}
                    onDragLeave={(e) => this.handleDragLeave(e, col.key)}
                    onDrop={(e) => this.handleDrop(e, col.key)}
                  >
                    <div className="kanban-column-header">
                      <span className="kanban-column-title">{col.label}</span>
                      <span className="kanban-column-count">{trueWipCount ?? colIssues.length}{wipLimit ? `/${wipLimit}` : ''}</span>
                    </div>
                    <div className={`kanban-column-body${dragOverColumn === col.key ? ' drag-over' : ''}`}>
                      {colIssues.length === 0 && (
                        <div className="kanban-column-empty">No issues</div>
                      )}
                      {colIssues.map((issue) => this.renderCard(issue))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Inline add form */}
          {!loading && !error && (
            <div className="kanban-add-form">
              <div className="kanban-add-row">
                <div className="kanban-add-field kanban-add-field--grow">
                  <label className="kanban-add-label">Title</label>
                  <input
                    className="kanban-add-input"
                    type="text"
                    value={this.state.newTitle}
                    onChange={(e) => this.setState({ newTitle: e.target.value, createError: null })}
                    placeholder="Add a new issue…"
                    onKeyDown={(e) => e.key === 'Enter' && this.createIssue()}
                  />
                </div>
                <div className="kanban-add-field kanban-add-field--grow">
                  <label className="kanban-add-label">Description</label>
                  <input
                    className="kanban-add-input"
                    type="text"
                    value={this.state.newDescription}
                    onChange={(e) => this.setState({ newDescription: e.target.value, createError: null })}
                    placeholder="Optional details"
                  />
                </div>
                <div className="kanban-add-field">
                  <label className="kanban-add-label">Chapter</label>
                  <select
                    className="kanban-add-select"
                    value={this.state.newChapterId}
                    onChange={(e) => this.setState({ newChapterId: e.target.value })}
                  >
                    <option value="">Project</option>
                    {this.state.chapters
                      .filter((ch) => !ch.archived_at)
                      .sort((a, b) => (a.chapter_number ?? 0) - (b.chapter_number ?? 0))
                      .map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          Ch {ch.chapter_number}{ch.title ? ` – ${ch.title}` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="kanban-add-field">
                  <label className="kanban-add-label">Severity</label>
                  <select
                    className="kanban-add-select"
                    value={this.state.newSeverity}
                    onChange={(e) => this.setState({ newSeverity: e.target.value })}
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="kanban-add-btn"
                  onClick={this.createIssue}
                  disabled={this.state.createBusy}
                >
                  {this.state.createBusy ? 'Adding…' : 'Add'}
                </button>
              </div>
              {this.state.createError && (
                <div className="kanban-add-error">{this.state.createError}</div>
              )}
            </div>
          )}
        </div>
        {this.renderDetailOverlay()}
        <MoveIssueToChapterModal
          isOpen={!!this.state.moveToChapterIssue}
          onClose={() => this.setState({ moveToChapterIssue: null, moveToChapterInitialChapterId: null })}
          issue={this.state.moveToChapterIssue}
          projectId={this.props.project?.id}
          chapters={this.state.chapters}
          initialChapterId={this.state.moveToChapterInitialChapterId}
          onMoved={(updated) => {
            const wasStructural = this.state.moveToChapterIssue?._source === 'structural'
              || this.state.moveToChapterIssue?._source === 'thematic';
            this.setState({ moveToChapterIssue: null, moveToChapterInitialChapterId: null });
            // Structural→chapter conversion creates a new chapter_issue (new id) and
            // removes the project_issue, so a full reload keeps the board consistent.
            if (wasStructural) {
              this.loadIssues();
              this.props.onIssuesCountChanged?.();
            } else {
              this.handleIssueUpdated(updated);
            }
          }}
        />
      </>
    );

    if (this.props.embedded) return innerContent;

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        {innerContent}
      </Modal>
    );
  }
}
